import {faArrowAltCircleRight} from '@fortawesome/pro-duotone-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography} from '@mui/material';
import React, {FC, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Change} from '../../utils/object-diff';
import {ChangeValueRenderer} from '../change-display';

export interface ConfirmChangesModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  changes: Record<string, Change>;
  title?: string;
  subtitle?: string;
}

export const ConfirmChangesModal: FC<ConfirmChangesModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  changes,
  title,
  subtitle,
}) => {
  const {t} = useTranslation();

  const changesCount = useMemo(() => Object.keys(changes).length, [changes]);
  const summaryText =
    changesCount === 1
      ? t('pages.pilotage-rh.indicators.changes_summary_singular')
      : t('pages.pilotage-rh.indicators.changes_summary', {count: changesCount});

  const yesLabel = t('buttons.yes', {defaultValue: 'Oui'});
  const noLabel = t('buttons.no', {defaultValue: 'Non'});

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle sx={{fontWeight: 600}}>{title || t('pages.pilotage-rh.indicators.confirm_changes')}</DialogTitle>
      <DialogContent dividers sx={{py: 1}}>
        <Stack spacing={3}>
          <Typography variant="body2" sx={{color: '#666'}}>
            {subtitle || summaryText}
          </Typography>

          <Stack spacing={2}>
            {Object.entries(changes).map(([fieldName, change]) => (
              <Stack
                key={fieldName}
                spacing={1.5}
                sx={{
                  p: 2,
                  backgroundColor: '#fafafa',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                }}
              >
                <Typography variant="subtitle2" sx={{fontWeight: 600, color: '#333'}}>
                  {fieldName}
                </Typography>

                <Stack direction="row" spacing={2} sx={{alignItems: 'flex-start'}}>
                  <Stack spacing={0.5} sx={{flex: 1, maxWidth: '375px'}}>
                    <Typography variant="caption" sx={{fontWeight: 500, color: '#999'}}>
                      {t('pages.pilotage-rh.indicators.before')}
                    </Typography>
                    <Stack
                      sx={{
                        p: 1,
                        backgroundColor: '#fff5f5',
                        borderRadius: 0.5,
                        border: '1px solid #fecaca',
                      }}
                    >
                      <ChangeValueRenderer
                        value={change.before}
                        comparedValue={change.after}
                        isBeforeColumn={true}
                        fieldType={change.type}
                        yesLabel={yesLabel}
                        noLabel={noLabel}
                      />
                    </Stack>
                  </Stack>

                  <Stack sx={{pt: 1, color: 'primary.main'}}>
                    <FontAwesomeIcon icon={faArrowAltCircleRight} size="xl" />
                  </Stack>

                  <Stack spacing={0.5} sx={{flex: 1, maxWidth: '375px'}}>
                    <Typography variant="caption" sx={{fontWeight: 500, color: '#999'}}>
                      {t('pages.pilotage-rh.indicators.after')}
                    </Typography>
                    <Stack
                      sx={{
                        p: 1,
                        backgroundColor: '#f0fdf4',
                        borderRadius: 0.5,
                        border: '1px solid #bbf7d0',
                      }}
                    >
                      <ChangeValueRenderer
                        value={change.after}
                        comparedValue={change.before}
                        isBeforeColumn={false}
                        fieldType={change.type}
                        yesLabel={yesLabel}
                        noLabel={noLabel}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{p: 2, gap: 1}}>
        <Button onClick={onClose} variant="outlined" disabled={isLoading}>
          {t('buttons.cancel')}
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary" disabled={isLoading}>
          {isLoading ? t('buttons.saving') : t('buttons.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
